import React from "react";
import { motion } from "framer-motion";
import JsonData from "../data/data.json";
import "./AboutUs.css";

const AboutUs = () => {
  const aboutData = JsonData.About;

  // ─── Merged Advantage Data ───
  const advantages = [
    {
      title: "A Vetted Supplier Network",
      image: "/img/Why Us/Go Team .png",
      desc: "Every order begins with a supplier whose quality record, capacity, and pricing are already established, before a meter of fabric is cut. No part of the learning happens on your order. And if one supplier turns out to be wrong for the job, we already know which one is right."
    },
    {
      title: "Product Development That Leads",
      image: "/img/Why Us/0cea7d379870d04b24b069383bb25ffd.png",
      desc: "Our development team works ahead of your calendar, proposing fabrics, washes, and constructions ahead of your requests. Send us a sketch or a reference and you receive costed options in return, so decisions can be made early in the season rather than late."
    },
    {
      title: "End-to-End Execution",
      image: "/img/Why Us/end-to-end-execution.jpg",
      desc: "One team owns your order from the first sample to the final shipment, so you have a single point of contact instead of a chain of handovers. The people who quoted the job are the same people who ship it, and they stay reachable throughout."
    },
    {
      title: "Independent Auditing",
      image: "/img/Why Us/Image by Scott Graham.png",
      desc: "Our quality teams report outside the production chain they inspect, so no department assesses its own output. Checks happen while the garment is still on the line, and every order gets a final audit before it is packed."
    }
  ];

  const timelineEvents = [
    { year: "2008", title: "The Founding", desc: "Softwood opens in Lahore as a sourcing house, on a simple promise: quality Pakistani textiles with someone clearly accountable for them. The early years established which suppliers could be relied on." },
    { year: "2014", title: "Manufacturing Integration", desc: "We add our own production floor. From this point we are not only recommending suppliers, we are making garments ourselves — which means we answer for the work from start to finish." },
    { year: "2019", title: "The Sustainable Shift", desc: "OEKO-TEX and Better Cotton certification is achieved across our primary supply chain, making sustainability a documented standard rather than a stated intention." },
    { year: "2025", title: "Proven at Scale", desc: "150,000+ garments a month, a 500-strong team, and a sourcing volume of $60 million — serving brands across more than 20 countries." }
  ];

  // ─── Framer Motion Variants ───
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  const popIn = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: "easeOut" } }
  };

  const slideRight = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut", delay: 0.2 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.14 } }
  };

  return (
    <div className="unified-about-page">

      {/* ── Hero ── */}
      <section className="compact-hero">
        <div className="container">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="compact-hero-content"
          >
            <span className="copper-eyebrow">About Softwood</span>
            <h1 className="hero-title-bold">
              A Network's Reach. <span className="hero-italic">A Maker's Control.</span>
            </h1>
            <ul className="about-hero-meta">
              <li>Since 2008</li>
              <li>500 people</li>
              <li>20+ countries</li>
            </ul>
          </motion.div>
        </div>

        {/* The photograph carries its own band rather than sitting behind the
            headline. It is forty people; text across it covers faces, and a
            scrim heavy enough to carry the type hides the group entirely. */}
        <motion.figure
          className="about-hero-photo"
          style={{ backgroundImage: `url('/img/About/team-hero.jpg')` }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.1, ease: "easeOut", delay: 0.25 }}
        >
          <figcaption className="about-hero-caption">
            <span className="about-hero-caption-rule" aria-hidden="true"></span>
            The team in Lahore
          </figcaption>
        </motion.figure>

        {/* Absorbed from the section that used to sit below the hero. Its own
            band, because .container is width-capped and the light ground has to
            run edge to edge under the navy above it. */}
        <div className="about-hero-intro-band">
        <motion.div
          className="container about-hero-intro"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <h2>Two Capabilities. One Standard.</h2>
          <div className="copper-divider center-div"></div>
          <div className="about-hero-intro-body">
            <p>{aboutData.paragraph}</p>
            <p>
              We invest continuously in our facilities and systems, and we run separate Quality Control and Quality Assurance departments side by side, so compliance and productivity never have to trade off. One team checks the garments; the other checks the process that made them. Separating the two costs more to run and saves considerably more in practice, because defects are identified on our floor rather than in your warehouse.
            </p>
          </div>
        </motion.div>
        </div>
      </section>

      {/* ── Editorial Overlap Advantage Section ── */}
      <section className="editorial-advantage-section">
        <div className="container">
          <motion.div className="text-center section-header" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2>The Softwood Advantage</h2>
            <div className="copper-divider center-div"></div>
            <p>The difference between working with Softwood and working from a list of suppliers. A directory can provide names; it cannot manage the order, check the work, or take responsibility when a date moves.</p>
          </motion.div>

          <div className="editorial-rows">
            {advantages.map((adv, index) => {
              const isEven = index % 2 !== 0;
              return (
                <div className={`adv-row ${isEven ? 'reverse-row' : ''}`} key={index}>
                  <motion.div
                    className="adv-img-col"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.4 }}
                    variants={fadeUp}
                  >
                    <div className="img-reveal-wrapper">
                      <img src={adv.image} alt={adv.title} />
                    </div>
                  </motion.div>

                  {/* fadeUp, not popIn: popIn scales the column, and a scaled
                      column renders narrower than its layout width, pulling the
                      text out of alignment with the image edge. A translate
                      cannot change width, so alignment always holds. */}
                  <motion.div
                    className="adv-text-col"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.4 }}
                    variants={fadeUp}
                  >
                    <div className="glass-card">
                      <span className="adv-number">0{index + 1}</span>
                      <div className="adv-rule"></div>
                      <h3>{adv.title}</h3>
                      <p>{adv.desc}</p>
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Custom Sleek Timeline ── */}
      <section className="custom-timeline-section">
        <div className="container">
          <motion.div className="text-center section-header" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2>Our Journey</h2>
            <div className="copper-divider center-div"></div>
          </motion.div>

          <motion.div
            className="milestones"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.div
              className="ms-rule"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, ease: "easeInOut" }}
            ></motion.div>

            {timelineEvents.map((event, index) => (
              <motion.div className="milestone" key={index} variants={fadeUp}>
                <span className="ms-node"></span>
                <span className="ms-year">{event.year}</span>
                <h4>{event.title}</h4>
                <p>{event.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default AboutUs;